import React from 'react';
import { useNavigate } from 'react-router-dom';
import SignatureThumbnail from './SignatureThumbnail';
import './SignatureTable.css';

function SignatureTable({
    signatures,
    selectedIds,
    onSelectSignature,
    onSelectAll,
    onSetDefault,
    onEdit,
    onDelete,
    onDuplicate,
    onToggleActive,
    onHistory,
    onPreview,
    loading,
    pendingChangesMap = {},
    canUpdate = true,
    canDelete = true
}) {
    const navigate = useNavigate();

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const allSelected = signatures.length > 0 && selectedIds.length === signatures.length;
    const someSelected = selectedIds.length > 0 && selectedIds.length < signatures.length;

    return (
        <div className="signature-table-container">
            <table className="signature-table">
                <thead>
                    <tr>
                        <th className="table-checkbox-col">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                ref={input => {
                                    if (input) {
                                        input.indeterminate = someSelected;
                                    }
                                }}
                                onChange={onSelectAll}
                                disabled={loading}
                            />
                        </th>
                        <th className="table-preview-col">Aperçu</th>
                        <th className="table-name-col">Nom</th>
                        <th className="table-date-col">Créée le</th>
                        <th className="table-date-col">Modifiée le</th>
                        <th className="table-creator-col">Créée par</th>
                        <th className="table-default-col">Par défaut</th>
                        <th className="table-status-col">Statut</th>
                        <th className="table-actions-col">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {signatures.length === 0 ? (
                        <tr>
                            <td colSpan="9" className="table-empty">
                                Aucune signature trouvée
                            </td>
                        </tr>
                    ) : (
                        signatures.map((signature) => (
                            <tr
                                key={signature.id}
                                className={`table-row ${selectedIds.includes(signature.id) ? 'table-row-selected' : ''}`}
                            >
                                <td className="table-checkbox-col">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(signature.id)}
                                        onChange={() => onSelectSignature(signature.id)}
                                        disabled={loading}
                                    />
                                </td>
                                <td className="table-preview-col">
                                    <SignatureThumbnail
                                        signatureId={signature.id}
                                        uid={signature.uid}
                                        updatedAt={signature.updated_at}
                                        width={100}
                                        height={60}
                                    />
                                </td>
                                <td className="table-name-col">
                                    <div className="table-name-wrapper">
                                        <span
                                            className="table-name-link"
                                            onClick={() => canUpdate ? onEdit(signature.id) : onPreview(signature.id)}
                                        >
                                            {signature.name}
                                        </span>
                                        {pendingChangesMap[signature.id] > 0 && (
                                            <span
                                                className="table-pending-badge"
                                                title={`${pendingChangesMap[signature.id]} modification(s) planifiée(s) en attente`}
                                            >
                                                ⏰ {pendingChangesMap[signature.id]}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="table-date-col">
                                    {formatDate(signature.created_at)}
                                </td>
                                <td className="table-date-col">
                                    {formatDate(signature.updated_at)}
                                </td>
                                <td className="table-creator-col">
                                    {signature.creator_first_name ? (
                                        <span className="table-creator">
                                            {signature.is_owner ? 'Vous' : `${signature.creator_first_name} ${signature.creator_last_name || ''}`}
                                        </span>
                                    ) : '-'}
                                </td>
                                <td className="table-default-col">
                                    {signature.is_default && (
                                        <span className="table-default-badge">
                                            ★
                                        </span>
                                    )}
                                </td>
                                <td className="table-status-col">
                                    <span className={`table-status-badge ${signature.is_active ? 'status-active' : 'status-inactive'}`}>
                                        {signature.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td className="table-actions-col">
                                    <div className="table-actions">
                                        {canUpdate && (
                                            <button
                                                className="table-action-btn table-action-edit"
                                                onClick={() => onEdit(signature.id)}
                                                title="Éditer"
                                                disabled={loading}
                                            >
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                    />
                                                </svg>
                                            </button>
                                        )}
                                        <button
                                            className="table-action-btn table-action-preview"
                                            onClick={() => onPreview(signature.id)}
                                            title="Aperçu"
                                            disabled={loading}
                                        >
                                            <svg
                                                width="16"
                                                height="16"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                />
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                />
                                            </svg>
                                        </button>
                                        {canUpdate && (
                                            <button
                                                className="table-action-btn table-action-settings"
                                                onClick={() => navigate(`/signatures/${signature.id}/settings`)}
                                                title="Paramètres"
                                                disabled={loading}
                                            >
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                                    />
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                    />
                                                </svg>
                                            </button>
                                        )}
                                        {canUpdate && !signature.is_default && (
                                            <button
                                                className="table-action-btn table-action-default"
                                                onClick={() => onSetDefault(signature.id)}
                                                title="Définir par défaut"
                                                disabled={loading}
                                            >
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                                                    />
                                                </svg>
                                            </button>
                                        )}
                                        {canUpdate && (
                                            <button
                                                className={`table-action-btn table-action-toggle ${signature.is_active ? 'active' : 'inactive'}`}
                                                onClick={() => onToggleActive(signature.id)}
                                                title={signature.is_active ? 'Désactiver' : 'Activer'}
                                                disabled={loading}
                                            >
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M13 10V3L4 14h7v7l9-11h-7z"
                                                    />
                                                </svg>
                                            </button>
                                        )}
                                        <button
                                            className="table-action-btn table-action-history"
                                            onClick={() => onHistory(signature.id, signature.name)}
                                            title="Historique"
                                            disabled={loading}
                                        >
                                            <svg
                                                width="16"
                                                height="16"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                        </button>
                                        {canUpdate && (
                                            <button
                                                className="table-action-btn table-action-duplicate"
                                                onClick={() => onDuplicate(signature.id)}
                                                title="Dupliquer"
                                                disabled={loading}
                                            >
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                    />
                                                </svg>
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button
                                                className="table-action-btn table-action-delete"
                                                onClick={() => onDelete(signature.id)}
                                                title="Supprimer"
                                                disabled={loading}
                                            >
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                    />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default SignatureTable;
